import type { ColumnDef, ListDef } from "./define";

/**
 * Converte una ColumnDef nel corpo `columnDefinition` di Microsoft Graph.
 * https://learn.microsoft.com/graph/api/resources/columndefinition
 */
export function toGraphColumn(
  col: ColumnDef,
  /** ID SharePoint degli elenchi già creati, per chiave logica (serve ai lookup). */
  listIds: Readonly<Record<string, string>> = {},
): Record<string, unknown> {
  const base: Record<string, unknown> = {
    name: col.name,
    displayName: col.displayName,
    description: col.description ?? "",
    required: col.required ?? false,
    indexed: col.indexed ?? false,
    enforceUniqueValues: false,
  };

  switch (col.kind) {
    case "text":
      return { ...base, text: { allowMultipleLines: false, maxLength: col.maxLength ?? 255 } };
    case "note":
      return {
        ...base,
        text: { allowMultipleLines: true, appendChangesToExistingText: false, linesForEditing: 6, textType: col.richText ? "richText" : "plain" },
      };
    case "number":
      return {
        ...base,
        number: {
          decimalPlaces: col.decimalPlaces ?? "automatic",
          displayAs: "number",
          ...(col.min !== undefined ? { minimum: col.min } : {}),
          ...(col.max !== undefined ? { maximum: col.max } : {}),
        },
      };
    case "currency":
      return { ...base, currency: { locale: col.locale ?? "it-IT" } };
    case "boolean":
      return {
        ...base,
        ...(col.defaultValue !== undefined ? { defaultValue: { value: col.defaultValue ? "1" : "0" } } : {}),
        boolean: {},
      };
    case "dateTime":
      return { ...base, dateTime: { displayAs: "default", format: col.format ?? "dateTime" } };
    case "choice":
      return {
        ...base,
        ...(col.defaultValue ? { defaultValue: { value: col.defaultValue } } : {}),
        choice: { allowTextEntry: col.allowTextEntry ?? false, choices: [...col.choices], displayAs: "dropDownMenu" },
      };
    case "person":
      return {
        ...base,
        personOrGroup: { allowMultipleSelection: col.allowMultiple ?? false, chooseFromType: "peopleOnly", displayAs: "nameWithPresence" },
      };
    case "lookup": {
      const listId = listIds[col.list];
      if (!listId) throw new Error(`Lookup ${col.name}: l'elenco "${col.list}" deve essere creato prima`);
      return {
        ...base,
        lookup: { listId, columnName: col.showField ?? "Title", allowMultipleValues: col.allowMultiple ?? false },
      };
    }
  }
}

/** Nomi delle colonne persona di una lista (servono alla mappatura LookupId). */
export function personColumns(list: ListDef): string[] {
  return list.columns.filter((c) => c.kind === "person").map((c) => c.name);
}

/** Nomi delle colonne lookup di una lista (stessa mappatura LookupId delle persone). */
export function lookupColumns(list: ListDef): string[] {
  return list.columns.filter((c) => c.kind === "lookup").map((c) => c.name);
}
